import { DetectionZone, Device, Facility, FacilityLayoutConfiguration, LaneLine, ScreenLine, Spot, Vector2 } from "@app/shared";
import JSZip from "jszip";

export const PLANNER_STORAGE_KEY = "sales-layout-planner-v1";
export const DEVICE_EXPORT_ZOOM = 19;
export const METERS_TO_FEET = 3.28084;

export type PlannerDraft = {
    locationName: string;
    latitude?: number;
    longitude?: number;
    facility: Facility;
};

type PlannerStorageModel = {
    locationName: string;
    latitude?: number;
    longitude?: number;
    devices?: Device[];
    detectionZones?: DetectionZone[];
    screenLines?: ScreenLine[];
    laneLines?: LaneLine[];
};

export type ImportedLocation = {
    name?: string;
    latitude: number;
    longitude: number;
};

const buildReferencePoint = (lat?: number, lon?: number) => {
    return new Spot({
        id: "sales-layout-reference-point",
        name: "Reference Point",
        points: [new Vector2({ x: lon ?? 0, y: lat ?? 0 })],
        origin: new Vector2({ x: lon ?? 0, y: lat ?? 0 }),
    } as any);
};

export const createFacility = (model: Partial<PlannerStorageModel>) => {
    return new Facility({
        id: "sales-layout-planner",
        name: model.locationName ?? "Sales Layout Plan",
        organizationId: "",
        devices: (model.devices ?? []).map((x) => new Device(x as any)),
        detectionZones: (model.detectionZones ?? []).map((x) => new DetectionZone(x as any)),
        screenLines: (model.screenLines ?? []).map((x) => new ScreenLine(x as any)),
        laneLines: (model.laneLines ?? []).map((x) => new LaneLine(x as any)),
        eventTriggers: [],
        originReferencePointLatLng: buildReferencePoint(model.latitude, model.longitude),
    } as any);
};

export const toLayoutConfiguration = (facility: Facility): FacilityLayoutConfiguration => {
    const config = new FacilityLayoutConfiguration();
    config.devices = (facility.devices ?? []).map((x) => new Device(x as any));
    config.detectionZones = (facility.detectionZones ?? []).map((x) => new DetectionZone(x as any));
    config.screenLines = (facility.screenLines ?? []).map((x) => new ScreenLine(x as any));
    config.laneLines = (facility.laneLines ?? []).map((x) => new LaneLine(x as any));
    config.referencePoint = facility.originReferencePointLatLng
        ? new Spot(facility.originReferencePointLatLng as any)
        : buildReferencePoint();
    return config;
};

export const loadDraftFromStorage = (): PlannerDraft => {
    const raw = localStorage.getItem(PLANNER_STORAGE_KEY);
    if (!raw) {
        return { locationName: "", facility: createFacility({}) };
    }

    try {
        const parsed = JSON.parse(raw) as PlannerStorageModel;
        return {
            locationName: parsed.locationName ?? "",
            latitude: parsed.latitude,
            longitude: parsed.longitude,
            facility: createFacility(parsed),
        };
    } catch {
        return { locationName: "", facility: createFacility({}) };
    }
};

export const saveDraftToStorage = (draft: PlannerDraft) => {
    const model: PlannerStorageModel = {
        locationName: draft.locationName,
        latitude: draft.latitude,
        longitude: draft.longitude,
        devices: draft.facility.devices ?? [],
        detectionZones: draft.facility.detectionZones ?? [],
        screenLines: draft.facility.screenLines ?? [],
        laneLines: draft.facility.laneLines ?? [],
    };
    localStorage.setItem(PLANNER_STORAGE_KEY, JSON.stringify(model));
};

export const sanitizeFileName = (value: string) =>
    (value || "sales-layout-plan")
        .trim()
        .replace(/[^a-zA-Z0-9-_ ]/g, "")
        .replace(/\s+/g, "_")
        .toLowerCase();

export const waitMs = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

// ── KML / KMZ import ────────────────────────────────────────────────

const parseCoordinatesText = (text: string): ImportedLocation | undefined => {
    const firstCoord = text
        .trim()
        .split(/\s+/)
        .find((s) => s.includes(","));
    if (!firstCoord) return undefined;

    const [lonRaw, latRaw] = firstCoord.split(",");
    const longitude = Number.parseFloat(lonRaw);
    const latitude = Number.parseFloat(latRaw);
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return undefined;
    return { latitude, longitude };
};

export const parseKmlLocations = (kmlText: string): ImportedLocation[] => {
    const doc = new DOMParser().parseFromString(kmlText, "application/xml");
    if (doc.querySelector("parsererror")) {
        throw new Error("Could not parse KML file.");
    }

    const fromPlacemarks = Array.from(doc.querySelectorAll("Placemark"))
        .map((pm) => {
            const coordNode = pm.querySelector("Point > coordinates");
            if (!coordNode?.textContent) return undefined;
            const parsed = parseCoordinatesText(coordNode.textContent);
            if (!parsed) return undefined;
            const nameNode = pm.querySelector("name");
            const name = nameNode?.textContent?.trim();
            return { ...parsed, name: name?.length ? name : undefined } as ImportedLocation;
        })
        .filter((x): x is ImportedLocation => !!x);

    if (fromPlacemarks.length) return fromPlacemarks;

    return Array.from(doc.querySelectorAll("Point > coordinates"))
        .map((n) => n.textContent)
        .filter((t): t is string => !!t)
        .map(parseCoordinatesText)
        .filter((x): x is ImportedLocation => !!x);
};

export const readKmlText = async (file: File): Promise<string> => {
    const lower = file.name.toLowerCase();
    if (lower.endsWith(".kml")) return file.text();
    if (!lower.endsWith(".kmz")) throw new Error("Only KML and KMZ files are supported.");

    const zip = await JSZip.loadAsync(await file.arrayBuffer());
    const kmlPath = Object.keys(zip.files).find((p) => p.toLowerCase().endsWith(".kml"));
    if (!kmlPath) throw new Error("KMZ file does not include a KML document.");
    const kmlFile = zip.file(kmlPath);
    if (!kmlFile) throw new Error("KMZ KML document could not be read.");
    return kmlFile.async("text");
};

export const averageLatLon = (locations: ImportedLocation[]) => {
    const sum = locations.reduce(
        (acc, l) => ({ latitude: acc.latitude + l.latitude, longitude: acc.longitude + l.longitude }),
        { latitude: 0, longitude: 0 }
    );
    return { latitude: sum.latitude / locations.length, longitude: sum.longitude / locations.length };
};
